import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaShoppingBag,
  FaArrowRight,
  FaWhatsapp,
  FaDownload,
} from "react-icons/fa";
import jsPDF from "jspdf";
import  API_BASE_URL  from "../config/api";
import "./OrderSuccess.css";

function OrderSuccess() {
  const location = useLocation();
  const navigate = useNavigate();

const {
  orderId,
  paymentMethod,
  total,
  product,
  quantity,
  cartItems,
  customer,
} = location.state || {};

/* ====
   ORDER ITEMS
==== */

const orderItems =
  cartItems && cartItems.length > 0
    ? cartItems
    : product
    ? [
        {
          ...product,
          quantity: Number(quantity || 1),
        },
      ]
    : [];

const deliveryCharges = 299;

const subtotal = orderItems.reduce(
  (sum, item) =>
    sum +
    Number(item.price || 0) *
      Number(item.quantity || 1),
  0
);

const qty = orderItems.reduce(
  (sum, item) =>
    sum + Number(item.quantity || 1),
  0
);

const grandTotal =
  Number(total ?? subtotal + deliveryCharges);

  /* ====
     PRODUCT IMAGE
  ==== */

const getProductImage = (item) => {
  if (!item?.image1) {
    return "/placeholder-watch.png";
  }

  if (item.image1.startsWith("http")) {
    return item.image1;
  }

  return `${API_BASE_URL}/${item.image1.replace(
    /^\/+/,
    ""
  )}`;
};

  /* ====
     CONTINUE SHOPPING
  ==== */

  const handleContinueShopping = () => {
    navigate("/");
  };

  /* ====
     WHATSAPP
  ==== */

const handleWhatsApp = () => {
  const orderDetails = orderItems
    .map(
      (item, index) => `
${index + 1}. ${item.name || item.title || "Time Valley Watch"}
Quantity: ${Number(item.quantity || 1)}
Price: Rs. ${(
        Number(item.price || 0) *
        Number(item.quantity || 1)
      ).toLocaleString()}
`
    )
    .join("\n");

  const message = `
Hello Time Valley,

I have successfully placed my order.

ORDER DETAILS
-------------------------
Order ID: #${orderId || "N/A"}

${orderDetails}

Subtotal: Rs. ${subtotal.toLocaleString()}
Delivery Charges: Rs. ${deliveryCharges.toLocaleString()}
Total Amount: Rs. ${grandTotal.toLocaleString()}

Payment Method: ${
    paymentMethod === "JAZZCASH"
      ? "JazzCash"
      : "Cash on Delivery"
  }

CUSTOMER DETAILS
-------------------------
Name: ${customer?.name || ""}
Phone: ${customer?.phone || ""}
Email: ${customer?.email || ""}

Thank you,
TIME VALLEY
`;

  const whatsappUrl =
    `https://wa.me/923000125398?text=` +
    encodeURIComponent(message);

  window.open(whatsappUrl, "_blank");
};

  /* ====
     DOWNLOAD PREMIUM ORDER SLIP
  ==== */

  const handleDownloadSlip = () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    /* -------------------------------------------------------
       COLORS
    ------------------------------------------------------- */

    const darkGreen = [18, 36, 25];
    const gold = [163, 132, 69];
    const cream = [243, 235, 221];
    const gray = [105, 105, 105];

    /* -------------------------------------------------------
       HEADER
    ------------------------------------------------------- */

    doc.setFillColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.rect(
      0,
      0,
      pageWidth,
      48,
      "F"
    );

    doc.setTextColor(
      207,
      175,
      107
    );

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(25);

    doc.text(
      "TIME VALLEY",
      pageWidth / 2,
      20,
      {
        align: "center",
      }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(9);

    doc.setTextColor(
      235,
      229,
      215
    );

    doc.text(
      "PREMIUM TIMEPIECES",
      pageWidth / 2,
      30,
      {
        align: "center",
      }
    );

    doc.setDrawColor(
      207,
      175,
      107
    );

    doc.line(
      72,
      36,
      138,
      36
    );

    /* -------------------------------------------------------
       ORDER CONFIRMED
    ------------------------------------------------------- */

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(19);

    doc.text(
      "ORDER CONFIRMATION",
      pageWidth / 2,
      65,
      {
        align: "center",
      }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(10);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

    doc.text(
      "Thank you for choosing Time Valley.",
      pageWidth / 2,
      73,
      {
        align: "center",
      }
    );

    /* -------------------------------------------------------
       ORDER ID BOX
    ------------------------------------------------------- */

    doc.setFillColor(
      cream[0],
      cream[1],
      cream[2]
    );

    doc.roundedRect(
      20,
      85,
      170,
      25,
      4,
      4,
      "F"
    );

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setFontSize(9);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

    doc.text(
      "ORDER ID",
      30,
      96
    );

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(14);

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.text(
      `#${orderId || "N/A"}`,
      30,
      104
    );

    const today = new Date().toLocaleDateString(
      "en-PK",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(9);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

    doc.text(
      "ORDER DATE",
      135,
      96
    );

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.text(
      today,
      135,
      104
    );

    /* -------------------------------------------------------
       CUSTOMER DETAILS
    ------------------------------------------------------- */

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(14);

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.text(
      "Customer Details",
      20,
      128
    );

    doc.setDrawColor(
      gold[0],
      gold[1],
      gold[2]
    );

    doc.line(
      20,
      132,
      190,
      132
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(9);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

    doc.text(
      `Name: ${customer?.name || "N/A"}`,
      20,
      143
    );

    doc.text(
      `Phone: ${customer?.phone || "N/A"}`,
      20,
      151
    );

    doc.text(
      `Email: ${customer?.email || "N/A"}`,
      20,
      159
    );

    doc.text(
      `City: ${customer?.city || "N/A"}`,
      110,
      143
    );

    doc.text(
      `Country: ${customer?.country || "Pakistan"}`,
      110,
      151
    );

    /* -------------------------------------------------------
       PRODUCT DETAILS
    ------------------------------------------------------- */

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(14);

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.text(
      "Order Details",
      20,
      181
    );

    doc.line(
      20,
      185,
      190,
      185
    );

    /* Table Header */

    doc.setFillColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.rect(
      20,
      193,
      170,
      12,
      "F"
    );

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setFontSize(8);

    doc.setTextColor(
      255,
      255,
      255
    );

    doc.text(
      "PRODUCT",
      25,
      201
    );

    doc.text(
      "QTY",
      120,
      201
    );

    doc.text(
      "PRICE",
      145,
      201
    );

    doc.text(
      "TOTAL",
      172,
      201
    );

   /* Product Rows */

doc.setTextColor(
  darkGreen[0],
  darkGreen[1],
  darkGreen[2]
);

doc.setFont(
  "helvetica",
  "normal"
);

doc.setFontSize(8);

let currentY = 216;

orderItems.forEach((item, index) => {
  const itemName =
    item?.name ||
    item?.title ||
    "Time Valley Watch";

  const shortItemName =
    itemName.length > 42
      ? itemName.substring(0, 42) + "..."
      : itemName;

  const itemQty =
    Number(item.quantity || 1);

  const itemPrice =
    Number(item.price || 0);

  const itemTotal =
    itemPrice * itemQty;

  doc.text(
    shortItemName,
    25,
    currentY
  );

  doc.text(
    String(itemQty),
    122,
    currentY
  );

  doc.text(
    `Rs. ${itemPrice.toLocaleString()}`,
    142,
    currentY
  );

  doc.text(
    `Rs. ${itemTotal.toLocaleString()}`,
    170,
    currentY
  );

  currentY += 10;
});

    /* -------------------------------------------------------
       TOTALS
    ------------------------------------------------------- */

    doc.setDrawColor(
      220,
      220,
      220
    );

 const totalsStartY =
  Math.max(
    227,
    currentY + 5
  );

doc.line(
  20,
  totalsStartY,
  190,
  totalsStartY
);
    doc.setFontSize(9);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

  const subtotalY = totalsStartY + 12;

doc.text(
  "Subtotal",
  125,
  subtotalY
);

doc.text(
  `Rs. ${subtotal.toLocaleString()}`,
  170,
  subtotalY
);


 const deliveryY = totalsStartY + 21;

doc.text(
  "Delivery Charges",
  125,
  deliveryY
);

doc.text(
  `Rs. ${deliveryCharges.toLocaleString()}`,
  170,
  deliveryY
);

    doc.setFillColor(
      cream[0],
      cream[1],
      cream[2]
    );

const grandTotalY = totalsStartY + 30;

doc.roundedRect(
  115,
  grandTotalY,
  75,
  18,
  3,
  3,
  "F"
);

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

  doc.text(
  "GRAND TOTAL",
  120,
  grandTotalY + 11
);

    doc.setTextColor(
      gold[0],
      gold[1],
      gold[2]
    );

  doc.text(
  `Rs. ${grandTotal.toLocaleString()}`,
  166,
  grandTotalY + 11
);

    /* -------------------------------------------------------
       PAYMENT
    ------------------------------------------------------- */

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(14);

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

   const paymentY = grandTotalY + 42;

doc.text(
  "Payment Information",
  20,
  paymentY
);
doc.line(
  20,
  paymentY + 4,
  190,
  paymentY + 4
);

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(9);

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

 doc.text(
  "Payment Method:",
  20,
  paymentY + 16
);

    doc.setFont(
      "helvetica",
      "bold"
    );

    doc.setTextColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

doc.text(
  paymentMethod === "JAZZCASH"
    ? "JazzCash"
    : "Cash on Delivery",
  65,
  paymentY + 16
);

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setTextColor(
      gray[0],
      gray[1],
      gray[2]
    );

   doc.text(
  "Order Status:",
  20,
  paymentY + 25
);

doc.setFont(
  "helvetica",
  "bold"
);

doc.setTextColor(
  45,
  120,
  70
);

doc.text(
  "Order Confirmed",
  65,
  paymentY + 25
);

    /* -------------------------------------------------------
       FOOTER
    ------------------------------------------------------- */

    doc.setFillColor(
      darkGreen[0],
      darkGreen[1],
      darkGreen[2]
    );

    doc.rect(
      0,
      350,
      pageWidth,
      47,
      "F"
    );

    doc.setFont(
      "times",
      "bold"
    );

    doc.setFontSize(14);

    doc.setTextColor(
      207,
      175,
      107
    );

    doc.text(
      "TIME VALLEY",
      pageWidth / 2,
      366,
      {
        align: "center",
      }
    );

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(8);

    doc.setTextColor(
      230,
      230,
      230
    );

    doc.text(
      "Thank you for shopping with Time Valley.",
      pageWidth / 2,
      376,
      {
        align: "center",
      }
    );

    doc.text(
      "Premium Timepieces • Timeless Elegance",
      pageWidth / 2,
      385,
      {
        align: "center",
      }
    );

    /* -------------------------------------------------------
       DOWNLOAD
    ------------------------------------------------------- */

    doc.save(
      `Time-Valley-Order-${orderId || "Receipt"}.pdf`
    );
  };

  return (
    <div className="tv-success-page">

      <div className="tv-success-glow tv-success-glow-one"></div>
      <div className="tv-success-glow tv-success-glow-two"></div>

      <div className="tv-success-container">

        <div className="tv-success-card">

          {/* SUCCESS ICON */}

          <div className="tv-success-icon-wrapper">
            <div className="tv-success-icon">
              <FaCheck />
            </div>
          </div>

          {/* CONTENT */}

          <div className="tv-success-content">

            <span className="tv-success-small-title">
              ORDER CONFIRMED
            </span>

            <h1>
              Thank You for Your
              <span> Order!</span>
            </h1>

            <div className="tv-success-line"></div>

            <p className="tv-success-message">
              Your order has been successfully placed.
              We truly appreciate your trust in
              <strong> Time Valley.</strong>
            </p>

            <p className="tv-success-submessage">
              Our team will carefully process your order
              and contact you shortly for confirmation.
            </p>

            {/* ORDER ID */}

            <div className="tv-order-id-box">

              <span>ORDER ID</span>

              <strong>
                #{orderId || "N/A"}
              </strong>

            </div>

            {/* INFO */}

            <div className="tv-success-info">

              <div className="tv-success-info-item">
                <span>Payment Method</span>

                <strong>
                  {paymentMethod === "JAZZCASH"
                    ? "JazzCash"
                    : "Cash on Delivery"}
                </strong>
              </div>

            <div className="tv-success-info-item">
  <span>Total Items</span>

  <strong>
    {qty}
  </strong>
</div>

              <div className="tv-success-info-item">
                <span>Total Amount</span>

                <strong className="tv-success-total">
                  Rs.{" "}
                  {grandTotal.toLocaleString()}
                </strong>
              </div>

            </div>

            {/* PRODUCT */}

          {/* PRODUCTS */}
{orderItems.length > 0 && (
  <div className="tv-success-products">

    {orderItems.map((item, index) => (
      <div
        className="tv-success-product"
        key={`${item.id}-${index}`}
      >

        {/* PRODUCT IMAGE */}
        <div className="tv-success-product-image">

          <img
            src={getProductImage(item)}
            alt={
              item.name ||
              item.title ||
              "Time Valley Watch"
            }
            onError={(e) => {
              e.currentTarget.src =
                "/placeholder-watch.png";
            }}
          />

        </div>

        {/* PRODUCT DETAILS */}
        <div className="tv-success-product-details">

          <span>
            YOUR PURCHASE
          </span>

          <h3>
            {item.name ||
              item.title ||
              "Time Valley Watch"}
          </h3>

          <p>
            Quantity:{" "}
            {Number(item.quantity || 1)}
          </p>

          <p>
            Price: {" "}
            {(
              Number(item.price || 0) *
              Number(item.quantity || 1)
            ).toLocaleString()}
          </p>

        </div>

      </div>
    ))}

  </div>
)}

            {/* NOTE */}

            <div className="tv-success-note">

              <FaShoppingBag />

              <p>
                Your timepiece is now on its way to
                becoming part of your collection.
              </p>

            </div>

            {/* BUTTONS */}

            <div className="tv-success-actions">

              <button
                type="button"
                className="tv-success-shopping-btn"
                onClick={handleDownloadSlip}
              >
                <FaDownload />

                <span>
                  Download Order Slip
                </span>
              </button>

              <button
                type="button"
                className="tv-success-shopping-btn"
                onClick={handleContinueShopping}
              >
                <span>
                  Continue Shopping
                </span>

                <FaArrowRight />
              </button>

              <button
                type="button"
                className="tv-success-whatsapp-btn"
                onClick={handleWhatsApp}
              >
                <FaWhatsapp />

                <span>
                  Contact on WhatsApp
                </span>
              </button>

            </div>

          </div>

          {/* FOOTER */}

          <div className="tv-success-footer">

            <span></span>

            <p>
              TIME VALLEY
            </p>

            <span></span>

          </div>

        </div>

      </div>

    </div>
  );
}

export default OrderSuccess;